import React, { useState } from 'react';
import { createStyles } from './AssignmentSidebar.styles';
import {
  Box,
  Button,
  Divider,
  Fab,
  Icons,
  TextField,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import NavigationBackHeader from '@app/components/presentational/common/NavigationBackHeader/NavigationBackHeader';
import { ASSIGNMENTS } from '@app/constants/texts';
import { ResponsiveAssignment } from '@core/assignments/types';
import AssignmentList from '../List/AssignmentList';
import AssignmentTools from './AssignmentTools';
import CreateAssignmentModal from '../CreateAssignment/CreateAssignmentModal';
import NoResultsCard from '@app/components/presentational/common/NoResults/NoResultsCard';
import { emptyAssignmentListText } from '../text';
import useAssignmentSearch from '../hooks/useAssignmentSearch';

type Props = {
  assignments: ResponsiveAssignment[];
  backRoute: string | null;
  onBackClick?: () => void;
  spaceId: string;
  spaceName?: string;
  baseDomain: string;
  isEducator?: boolean;
  isDesktop: boolean;
  onMaterialClick?: (materialId: string) => void;
  fetchAccessToken?: (spaceId: string) => Promise<string | null>;
  fetchAssignments?: () => Promise<void>;
  highlightedAssignmentId?: string | null;
};

const AssignmentSidebar = ({
  assignments,
  backRoute,
  onBackClick,
  spaceId,
  spaceName,
  baseDomain,
  isEducator = false,
  isDesktop,
  onMaterialClick,
  fetchAccessToken,
  fetchAssignments,
  highlightedAssignmentId,
}: Props) => {
  const theme = useTheme();
  const styles = createStyles(theme, isDesktop);
  // States for the assignment creation modal
  const [assignmentStates, setAssignmentStates] = useState({
    editingAssignment: null,
    isCreating: false,
    isCoupled: false,
  } as {
    editingAssignment: any;
    isCreating: boolean;
    isCoupled: boolean;
  });

  // All styles for the assignment tools component
  const assignmentToolStyles = {
    assignmentToolsWrapper: {
      ...styles.assignmentToolsWrapper,
      background: isDesktop
        ? theme?.palette?.background?.default
        : theme.palette.background.paper,
    },
    common: styles.common,
    avatarStyle: styles.avatarStyle,
    newTabIcon: styles.newTabIcon,
    toolTitle: styles.toolTitle,
    tooltipStyles: styles.tooltipStyles,
    modalContainer: styles.modalContainer,
    modalHeader: styles.modalHeader,
    modalContent: styles.modalContent,
    modalFooter: styles.modalFooter,
    modalCardTitle: styles.modalCardTitle,
    modalCardDescription: styles.modalCardDescription,
  };

  const handleClearEditModal = () => {
    setAssignmentStates((prev) => ({
      ...prev,
      editingAssignment: null,
    }));
  };

  const handleToggleCreateAssignment = () => {
    setAssignmentStates((prev) => ({
      ...prev,
      isCreating: !prev.isCreating,
    }));
    handleClearEditModal();
  };

  // Open CreateAssignmentModal in edit mode for Turnitin assignments
  const handleTurnitinClick = (assignment: ResponsiveAssignment) => {
    setAssignmentStates({
      editingAssignment: assignment,
      isCreating: true,
      isCoupled: false,
    });
  };

  const { isCreating, isCoupled } = assignmentStates;

  const {
    inputValue,
    isSearching,
    filteredAssignments,
    hasActiveQuery,
    handleChange,
    handleClear,
  } = useAssignmentSearch(assignments);

  const noResults = filteredAssignments.length === 0;

  return (
    <Box sx={styles.sidebarWrapper}>
      <Box sx={styles.buttonWrapper}>
        <NavigationBackHeader route={backRoute} onClick={onBackClick} />
      </Box>
      <Box sx={styles.sidebarContent}>
        <Box sx={styles.headerWrapper}>
          <Box sx={styles.headerIcon} aria-hidden>
            <Icons.AssessmentOutlined fontSize="medium" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="h2" variant="h6" sx={styles.title}>
              {ASSIGNMENTS}
            </Typography>
          </Box>
          {isEducator && (
            <Box sx={styles.mobileCreateButton}>
              <Fab
                variant="circular"
                size="small"
                color="primary"
                onClick={handleToggleCreateAssignment}
                aria-label="Create new assignment"
              >
                <Icons.Add />
              </Fab>
            </Box>
          )}
        </Box>
        {spaceName && (
          <Typography sx={styles.spaceName}>{spaceName}</Typography>
        )}

        <Divider sx={{ my: '0px' }} />

        {isEducator && (
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Button
              onClick={handleToggleCreateAssignment}
              type="button"
              kind="primary"
              sx={styles.createButton}
            >
              <Icons.Add />
              New assignment
            </Button>
          </Box>
        )}
        <Box>
          <AssignmentTools
            styles={assignmentToolStyles}
            baseDomain={baseDomain}
            spaceId={spaceId}
            isEducator={isEducator}
            isDesktop={isDesktop}
          />
        </Box>
        <Box sx={{ ...styles.searchWrapper, position: 'relative' }}>
          <TextField
            label=""
            placeholder="Search for assignments"
            value={inputValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(e.target.value)
            }
            leftElement={<Icons.Search sx={{ color: 'text.secondary' }} />}
            sx={{
              m: 0,
              width: '100%',
              '& .MuiFormHelperText-root': { display: 'none' },
              ...(inputValue
                ? { '& .MuiInputBase-input': { paddingRight: '40px' } }
                : {}),
            }}
            aria-label="Search for assignments"
          />
          {inputValue && (
            <Box
              component="button"
              onClick={handleClear}
              aria-label="Clear search"
              sx={styles.clearSearchButton}
            >
              <Icons.Close sx={{ color: 'text.secondary' }} />
            </Box>
          )}
        </Box>
        <Divider sx={{ my: '16px' }} />
        <Box sx={styles.listWrapper}>
          {isSearching ? (
            <AssignmentList
              assignments={filteredAssignments}
              spaceId={spaceId}
              isEducator={isEducator}
              highlightedAssignmentId={highlightedAssignmentId}
              onMaterialClick={onMaterialClick}
              onTurnitinClick={handleTurnitinClick}
            />
          ) : noResults ? (
            <NoResultsCard
              title={
                hasActiveQuery
                  ? 'Oops! No matches'
                  : emptyAssignmentListText.title
              }
              description={
                hasActiveQuery
                  ? 'No assignments match your search.'
                  : emptyAssignmentListText.description
              }
              sx={styles.noResultsCard}
              titleSx={styles.noResultsCardTitle}
              descriptionSx={styles.noResultsCardDescription}
              icon={<Icons.AssignmentOutlined fontSize="medium" aria-hidden />}
            />
          ) : (
            <AssignmentList
              assignments={filteredAssignments}
              spaceId={spaceId}
              isEducator={isEducator}
              highlightedAssignmentId={highlightedAssignmentId}
              onMaterialClick={onMaterialClick}
              onTurnitinClick={handleTurnitinClick}
            />
          )}
        </Box>
        <CreateAssignmentModal
          key={`create-assignment-modal-${isCreating}-${
            assignmentStates.editingAssignment?.objectId ??
            assignmentStates.editingAssignment?.id
          }`}
          isVisible={isCreating && !isCoupled}
          onClose={handleToggleCreateAssignment}
          editingAssignment={assignmentStates.editingAssignment}
          currentSpace={{ objectId: spaceId }}
          fetchAccessToken={
            fetchAccessToken
              ? (spaceId: string) =>
                  fetchAccessToken(spaceId).then((t) => t ?? '')
              : async () => ''
          }
          fetchAssignments={fetchAssignments ?? (async () => {})}
        />
      </Box>
    </Box>
  );
};

export default AssignmentSidebar;
